<template>
  <section
    class="PresentationBlock-background"
    :class="
      background ? `PresentationBlock-background--${background}` : undefined
    ">
    <article class="PresentationBlock Layout">
      <div class="Layout-section">
        <grid-item
          v-bind="descriptionGridItemBinds"
          class="PresentationBlock-teaserContainer">
          <ai-typo
            as="h2"
            variant="heading-02"
            class="PresentationBlock-teaserTitle">
            {{ title }}
          </ai-typo>

          <ai-typo
            v-if="shortText"
            :id="`description-` + slugify(title)"
            as="p"
            color="default"
            variant="paragraph-03-regular"
            :class="
              expanded
                ? `PresentationBlock-teaserText--full`
                : `PresentationBlock-teaserText`
            ">
            {{ displayText }}
          </ai-typo>

          <ai-typo
            v-else
            :id="`description-` + slugify(title)"
            as="p"
            color="default"
            variant="paragraph-03-regular"
            :class="
              expanded
                ? `PresentationBlock-teaserText--full`
                : `PresentationBlock-teaserText`
            ">
            {{ displayText }}
          </ai-typo>

          <ai-button
            v-if="shortText"
            :class="[
              'AiPrivateEvent-button--chevron',
              { 'AiPrivateEvent-button--chevron-expanded': expanded },
            ]"
            class="PresentationBlock-readMore"
            :label="buttonLabel"
            :variant="AiButtonVariant.Tertiary"
            :icon-right="'chevron' as IconName"
            slim
            :aria-expanded="expanded"
            :aria-controls="`description-` + slugify(title)"
            :aria-label="readMoreAriaLabel"
            @click="toggleExpanded" />

          <slot />
        </grid-item>
        <grid-item v-bind="carouselGridItemBinds">
          <ai-carousel
            v-model="currentStep"
            :aria-label="title"
            :items="items"
            :access-id="accessId"
            :mobile-button="mobileButton"
            :hide-progress-bar="hideProgressBar"
            class="PresentationBlock-carousel"
            :slide-width="slideWidth"
            :gutter="gutter"
            :position="inverse ? 'left' : 'right'"
            arrows-top-position="15%"
            :button-accessibility-label="title"
            :gallery-on-carousel-click="galleryOnCarouselClick"
            @open-media-gallery="galleryOpened = true" />
        </grid-item>

        <ai-media-gallery
          v-model="galleryOpened"
          v-model:step="currentStep"
          :aria-label="title"
          :items="items"
          :gallery-component="AiImage"
          :button-accessibility-label="title"
          @focus-gallery-button="galleryButton" />
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import AiButton from '../../atoms/AiButton/AiButton.vue';
import { AiButtonVariant } from '../../atoms/AiButton/constants';
import type { IconName } from '../../atoms/AiIcon/types';
import AiImage from '../../atoms/AiImage/AiImage.vue';
import AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import AiCarousel from '../../molecules/AiCarousel/AiCarousel.vue';
import AiCarouselImageItem from '../../molecules/AiCarousel/AiCarouselImageItem.vue';
import type { AiCarouselItem } from '../../molecules/AiCarousel/types';
import AiMediaGallery from '../../molecules/AiMediaGallery/AiMediaGallery.vue';

import { slugify } from '~~/helpers/index';

interface AiPresentationBlockProps {
  accessId: string;
  title: string;
  description: string;
  modelValue: number;
  mobileButton?: boolean;
  hideProgressBar?: boolean;
  items: Record<string, unknown>[];
  galleryComponent?: Component | keyof HTMLElementTagNameMap;
  background?: 'gold' | 'stratos' | 'primary' | 'neutral';
  inverse?: boolean;
  gutter?: number;
  slideWidth?: number;
  galleryOnCarouselClick?: boolean;
}

interface Emits {
  (event: 'update:modelValue', step: number): void;
}

const props = withDefaults(defineProps<AiPresentationBlockProps>(), {
  background: undefined,
  hideProgressBar: false,
  galleryComponent: AiCarouselImageItem,
  gutter: 16,
  mobileButton: false,
  inverse: false,
  slideWidth: 1,
});

const emits = defineEmits<Emits>();

const items = computed<AiCarouselItem[]>(() =>
  (props.items ?? []).map(item => ({
    component: props.galleryComponent,
    key: `with-description-${props.title + (item.key ?? item.src)}`,
    // this is the desktop aspect ratio, the mobile is resized
    props: { ...item, fit: 'fill' },
  })),
);

const currentStep = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
});

const { t } = useI18n();
const { expanded, shortText, displayText, toggleExpanded } = useExpandableText(
  props.description,
  250,
  300,
);

const buttonLabel = computed<string>(() =>
  expanded.value
    ? t('ux.organisms.carouselWithDescription.description.readLessCTA')
    : t('ux.organisms.carouselWithDescription.description.readMoreCTA'),
);

const isSmallDevice = useMatchMediaQuery('tablet');

// Adjust grid configuration depending on the layout (either carousel first or description)
const descriptionGridItemBinds = computed(() => ({
  md: 4,
  offsetMd: 1,
  order: props.inverse && !isSmallDevice.value ? 2 : 1,
  sm: 4,
}));

const carouselGridItemBinds = computed(() => ({
  md: 5,
  offsetMd: 1,
  order: props.inverse && !isSmallDevice.value ? 1 : 2,
  sm: 4,
}));

const galleryOpened = ref<boolean>(false);

const galleryButton = () => {
  const buttonId = props.accessId + '-galleryButton';
  const target = document.getElementById(buttonId);
  if (target) {
    target.focus();
  }
};

const readMoreAriaLabel = computed(() => props.title + ' ' + buttonLabel.value);
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';

.PresentationBlock-background {
  background: colors.$white;

  &--neutral {
    background: colors.$neutral-100;
  }

  &--gold {
    background: colors.$gold-100;
  }

  &--stratos,
  &--primary {
    background: constants.$linear-gradient-stratos;
  }
}

.PresentationBlock {
  padding-top: constants.$padding-07;
  padding-bottom: constants.$padding-07;
}

.PresentationBlock-teaserTitle {
  margin-bottom: constants.$margin-02;
}

.PresentationBlock-teaserContainer {
  @media (mq.$upto-medium) {
    margin-bottom: constants.$margin-04;
  }
}

.PresentationBlock-readMore {
  margin-top: constants.$margin-03;
}

.PresentationBlock-carousel {
  @media (mq.$from-medium) {
    flex-shrink: 0;
    margin-left: 0;
  }
}
</style>
